import React from 'react';
import { Column } from 'react-table';
import qs from 'querystring';
import { CobuCheckbox } from '~/components/CobuCheckbox';
import { ReactComponent as Comments } from '~/utils/images/comments.svg';
import { ImageModerationAction, ImageModerationTab } from '~/api/admin';
import classes from './ImageModerationContainer.module.css';
import {
  ModeratedImage,
  Thresholds,
  categoryLabel,
  PEOPLE_CATEGORY
} from './types';

// `scanVerdict` is the verdict; `hidden` is what residents actually see. The two
// diverge outside enforce, where verdicts are recorded but never applied — so a
// label naming a verdict must check `hidden` before claiming the photo is gone.
const stateLabel = (image: ModeratedImage) => {
  switch (image.scanVerdict) {
    case 'auto_hidden':
      return image.hidden ? 'AUTO-HIDDEN' : 'WOULD HIDE';
    case 'scan_failed':
      return image.hidden ? 'SCAN FAILED' : 'SCAN FAILED (SHOWING)';
    case 'questionable':
      // A staff-hidden photo keeps its frozen 'questionable' verdict.
      return image.hidden ? 'HIDDEN' : 'SHOWING';
    default:
      return image.scanVerdict.toUpperCase();
  }
};

const confidenceClass = (confidence: number, thresholds: Thresholds) =>
  confidence >= thresholds.autoHide ? classes.redText : classes.amberText;

export const getColumnsForImageModerationTable = ({
  tab,
  thresholds,
  selectedIds,
  onToggleSelect,
  onEnlarge,
  onShowDetails,
  action,
  onReviewAboveClicked
}: {
  tab: ImageModerationTab;
  thresholds: Thresholds;
  selectedIds: string[];
  onToggleSelect: (uuid: string) => void;
  onEnlarge: (image: ModeratedImage) => void;
  onShowDetails: (image: ModeratedImage) => void;
  action: (type: ImageModerationAction, image: ModeratedImage) => void;
  onReviewAboveClicked: (image: ModeratedImage) => void;
}): Column<any>[] => {
  const columns: Column<any>[] = [];

  // Bulk select, needs-review tab only.
  if (tab === 'review') {
    columns.push({
      Header: '',
      id: 'select',
      disableSortBy: true,
      width: '28px',
      accessor: (row: ModeratedImage) => (
        <div className={`${classes.tableColumn} ${classes.selectColumn}`}>
          <CobuCheckbox
            checked={selectedIds.includes(row.uuid)}
            onChange={() => onToggleSelect(row.uuid)}
          />
        </div>
      )
    });
  }

  columns.push(
    {
      Header: 'Actions',
      id: 'actions',
      disableSortBy: true,
      accessor: (row: ModeratedImage) => (
        <div className={`${classes.tableColumn} ${classes.actionsColumn}`}>
          {/* Driven by `hidden`, not the tab: outside enforce a verdict is
              recorded without being applied, so the AUTO-HIDDEN tab can hold a
              photo residents can still see. Offer the action that changes
              something. */}
          {row.hidden ? (
            <button
              className={classes.greenText}
              onClick={() => action('UNHIDE', row)}
            >
              UNHIDE
            </button>
          ) : (
            <button
              className={classes.redText}
              onClick={() => action('HIDE', row)}
            >
              HIDE
            </button>
          )}
          <button
            className={classes.reviewButton}
            onClick={() => action('MARK_REVIEWED', row)}
          >
            REVIEW
          </button>
          <button
            className={classes.reviewButton}
            onClick={() => onShowDetails(row)}
          >
            DETAILS
          </button>
          <button
            id={`reviewAboveButton-${row.uuid}`}
            className={classes.reviewAboveButton}
            onClick={() => onReviewAboveClicked(row)}
          >
            REVIEW ABOVE
          </button>
        </div>
      )
    },
    {
      Header: 'Image',
      id: 'image',
      disableSortBy: true,
      width: '120px',
      accessor: (row: ModeratedImage) => (
        // An <img>, not a CSS background: interpolating the URL into `url(...)`
        // is what forced the separate imageDetails field on the comment table.
        <div className={classes.tableColumn}>
          <img
            src={row.image}
            alt='Moderated upload'
            className={classes.thumbnail}
            onClick={() => onEnlarge(row)}
          />
        </div>
      )
    },
    {
      Header: 'Matched categories',
      id: 'categories',
      disableSortBy: true,
      width: '260px',
      accessor: (row: ModeratedImage) => {
        if (!row.matchedCategories.length) {
          return (
            <div
              className={`${classes.tableColumn} ${classes.categoryColumn} ${classes.mutedText}`}
            >
              {row.scanVerdict === 'scan_failed'
                ? 'No verdict — scan failed'
                : '—'}
            </div>
          );
        }

        return (
          <div className={`${classes.tableColumn} ${classes.categoryColumn}`}>
            {row.matchedCategories.map((cat) => (
              <div key={cat.key} className={classes.categoryRow}>
                <span
                  className={confidenceClass(cat.confidence, thresholds)}
                  title={cat.reason}
                >
                  {categoryLabel(cat.key)} {Math.round(cat.confidence)}%
                </span>
                {cat.isHideReason && (
                  <span className={classes.hideReasonBadge}>HID</span>
                )}
                {/* People never auto-hides, so a high score is a flag only. */}
                {cat.key === PEOPLE_CATEGORY && !cat.isHideReason && (
                  <span className={classes.mutedBadge}>flag only</span>
                )}
              </div>
            ))}
          </div>
        );
      }
    },
    {
      Header: 'State',
      id: 'state',
      disableSortBy: true,
      accessor: (row: ModeratedImage) => (
        <div
          className={`${classes.tableColumn} ${classes.smallColumn} ${
            row.hidden ? classes.redText : classes.greenText
          }`}
        >
          {stateLabel(row)}
        </div>
      )
    },
    {
      Header: 'Uploader',
      id: 'author',
      disableSortBy: true,
      accessor: (row: ModeratedImage) => (
        <div className={`${classes.tableColumn} ${classes.userName}`}>
          {row.authorName}
        </div>
      )
    },
    {
      Header: 'Building',
      id: 'building',
      disableSortBy: true,
      accessor: (row: ModeratedImage) => (
        <div className={`${classes.tableColumn} ${classes.building}`}>
          {row.buildingName || '—'}
        </div>
      )
    },
    {
      Header: 'Text content',
      id: 'post',
      disableSortBy: true,
      width: '240px',
      accessor: (row: ModeratedImage) => {
        // Event-comment photos have no dedicated admin view, so only group
        // comments and Marketplace photos get the link icon. The text shows
        // either way.
        const isMarketplacePhoto =
          row.parentType === 'listing' || row.parentType === 'listingComment';
        const linkable =
          row.buildingId &&
          (row.parentType === 'groupComment' || isMarketplacePhoto);
        const href = isMarketplacePhoto
          ? `/building/${row.buildingId}/listings`
          : `/building/${row.buildingId}/comments?${qs.stringify({
              commentId: row.parentUuid,
              ...(row.groupId && { groupId: row.groupId })
            })}`;

        return (
          <div className={`${classes.tableColumn} ${classes.postColumn}`}>
            {linkable && (
              <a
                href={href}
                className='btn-ghost'
                target='_blank'
                rel='noopener noreferrer'
              >
                <Comments height={20} width={20} />
              </a>
            )}
            <span
              className={classes.postContent}
              title={row.parentContent ?? ''}
            >
              {row.parentContent || '—'}
            </span>
          </div>
        );
      }
    },
    {
      Header: 'Uploaded',
      id: 'when',
      disableSortBy: true,
      accessor: (row: ModeratedImage) => (
        <div className={classes.tableColumn}>{row.createdAt.displayValue}</div>
      )
    }
  );

  return columns;
};
